import { forwardRef } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  selectSize?: 'sm' | 'md' | 'lg';
  /** Convenience alternative to writing `<option>` children. */
  options?: readonly SelectOption[];
  /** Rendered as a disabled first option when the value is empty. */
  placeholder?: string;
  leadingIcon?: ReactNode;
  wrapperClassName?: string;
}

const SIZES = {
  sm: 'h-8 text-[13px]',
  md: 'h-9 text-sm',
  lg: 'h-11 text-[15px]',
} as const;

/**
 * Native select styled to match the design system. Native beats a custom
 * listbox here: correct keyboard behaviour and a real picker on mobile.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    invalid,
    selectSize = 'md',
    options,
    placeholder,
    leadingIcon,
    wrapperClassName,
    children,
    ...props
  },
  ref,
) {
  return (
    <div className={cn('relative w-full', wrapperClassName)}>
      {leadingIcon && (
        <span
          className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-subtle [&_svg]:size-4"
          aria-hidden
        >
          {leadingIcon}
        </span>
      )}
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full appearance-none rounded-md border border-border bg-surface pr-9 text-fg',
          'transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35',
          'disabled:cursor-not-allowed disabled:bg-elevated disabled:opacity-60',
          SIZES[selectSize],
          leadingIcon ? 'pl-9' : 'pl-3',
          invalid && 'border-danger focus-visible:border-danger focus-visible:ring-danger/30',
          className,
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-subtle"
        aria-hidden
      />
    </div>
  );
});
